import { useState, useEffect, useCallback } from 'react';
import { logger } from '../utils/debugLogger';
import medicalReservationApiService from '../services/medicalReservationApiService';
import { handleApiError } from '../utils/apiClient';
import { MedicalTimeRange } from '../types/csvUploader';
import { isTimeRangeComplete } from '../utils/timeRangeFilter';
import type {
  MedicalReservationRecord,
  MedicalReservationListResponse,
  MedicalReservationFilterOptions,
  OutOfHoursMedicalReservationFilterOptions,
  MedicalReservationListState,
  ActionStatus,
  PasteStatusResponse,
} from '../types/medicalReservation';

/**
 * 指定時間外診療予約一覧管理カスタムフック
 *
 * 意図：
 * - 指定時間外の診療予約データの取得と管理
 * - 過去の診療開始時間の判定と表示制御
 * - ページング、ソート、フィルタリング機能
 *
 * 注意点：
 * - APIエンドポイントは /api/medical-reservations/out-of-hours を使用
 * - 過去の診療データの適切な表示制御
 * - メモリリークの防止（cleanup処理）
 *
 * 改善提案：
 * - キャッシュ機能の実装
 * - 楽観的更新の実装
 * - WebSocketによるリアルタイム更新
 */
export const useOutOfHoursMedicalReservationList = () => {
  // 時間範囲をLocalStorageから取得
  const getStoredTimeRange = useCallback((): MedicalTimeRange | null => {
    const stored = localStorage.getItem('mederi_csv_time_range');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return null;
      }
    }
    return null;
  }, []);

  const [state, setState] = useState<MedicalReservationListState>({
    data: [],
    meta: null,
    isLoading: false,
    error: null,
    filters: {
      // デフォルトで時間外フィルタを有効化
      isOutOfHours: true,
    } as OutOfHoursMedicalReservationFilterOptions,
    selectedRecord: null,
    detailModalOpen: false,
    editModalOpen: false,
    assignModalOpen: false,
    selectedIds: [],
  });

  // 貼り付けダイアログの状態管理
  const [pasteDialogOpen, setPasteDialogOpen] = useState(false);
  const [pasteStatus, setPasteStatus] = useState<PasteStatusResponse['data'] | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  /**
   * 診療開始時間が過去かどうか判定
   */
  const isPastConsultation = useCallback((consultationStartTime: string): boolean => {
    const now = new Date();
    const consultationTime = new Date(consultationStartTime);
    return consultationTime < now;
  }, []);

  /**
   * データ取得（LocalStorageから）
   */
  const fetchData = useCallback(async () => {
    const timer = logger.startTimer('useOutOfHoursMedicalReservationList', 'fetchData');

    logger.info('useOutOfHoursMedicalReservationList', 'fetchData:開始（LocalStorageから読み込み）');

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      // LocalStorageから時間外データを取得
      const storedData = localStorage.getItem('mederi_outside_time_range_data');

      if (!storedData) {
        // データがない場合
        logger.info('useOutOfHoursMedicalReservationList', 'LocalStorageにデータがありません');

        setState(prev => ({
          ...prev,
          data: [],
          meta: {
            total_records: 0,
            current_page: 1,
            page_size: 10,
            total_pages: 1
          },
          isLoading: false,
        }));

        timer({
          success: true,
          recordCount: 0
        });
        return;
      }

      const parsedData = JSON.parse(storedData);

      logger.info('useOutOfHoursMedicalReservationList', 'LocalStorageからデータを読み込みました', {
        データ件数: parsedData.length
      });

      // データ型の変換（CSVデータ形式 → MedicalReservationRecord形式）
      const convertedData = parsedData.map((record: any, index: number) => {
        // CSVヘッダー名からAPIフィールド名への変換
        const consultationStartTime = record['診療開始時間'] || record.consultationStartTime || '';

        return {
          id: `temp-${index}`, // 一時的なID
          anybotId: record['anybot ID'] || record.anybotId || '',
          consultationStartTime: consultationStartTime,
          doctorInCharge: record['担当医師'] || record.doctorInCharge || '',
          name: record['名前（漢字）'] || record.name || '',
          nameKana: record['名前（カナ）'] || record.nameKana || '',
          phoneNumber: record['電話番号'] || record.phoneNumber || '',
          status: record['ステータス'] || record.status || '',
          counselorResponse: record['カウンセラー対応'] || record.counselorResponse || '',
          isPast: isPastConsultation(consultationStartTime),
          // notificationCheckerで使用されるcsvDataプロパティを追加
          csvData: record,
          // その他の必要なフィールドをマッピング
          ...record
        };
      });

      logger.info('useOutOfHoursMedicalReservationList', 'fetchData:成功', {
        recordCount: convertedData.length,
        pastRecordCount: convertedData.filter((r: any) => r.isPast).length
      });

      setState(prev => ({
        ...prev,
        data: convertedData,
        meta: {
          total_records: convertedData.length,
          current_page: 1,
          page_size: convertedData.length,
          total_pages: 1
        },
        isLoading: false,
        error: null,
      }));

      timer({
        success: true,
        recordCount: convertedData.length
      });
    } catch (error) {
      const errorMessage = 'データの読み込みに失敗しました';

      logger.error('useOutOfHoursMedicalReservationList', 'fetchData:エラー', error, {
        errorMessage
      });

      setState(prev => ({
        ...prev,
        data: [],
        meta: null,
        isLoading: false,
        error: errorMessage,
      }));

      timer({
        success: false,
        error: errorMessage
      });
    }
  }, [isPastConsultation]);

  /**
   * フィルター更新
   */
  const updateFilters = useCallback((newFilters: Partial<OutOfHoursMedicalReservationFilterOptions>) => {
    logger.debug('useOutOfHoursMedicalReservationList', 'updateFilters', {
      currentFilters: state.filters,
      newFilters
    });

    setState(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        ...newFilters,
        // ページングリセット（新しいフィルタ適用時）
        page: newFilters.page || 1,
      } as OutOfHoursMedicalReservationFilterOptions,
    }));
  }, [state.filters]);

  /**
   * ページ変更
   */
  const handlePageChange = useCallback((page: number) => {
    updateFilters({ page });
  }, [updateFilters]);

  /**
   * ページサイズ変更
   */
  const handlePageSizeChange = useCallback((pageSize: number) => {
    updateFilters({ page_size: pageSize, page: 1 });
  }, [updateFilters]);

  /**
   * ソート変更
   */
  const handleSort = useCallback((sortBy: keyof MedicalReservationRecord, sortOrder: 'asc' | 'desc') => {
    updateFilters({ sort_by: sortBy, sort_order: sortOrder });
  }, [updateFilters]);

  /**
   * 検索
   */
  const handleSearch = useCallback((search: string) => {
    updateFilters({ search, page: 1 });
  }, [updateFilters]);

  /**
   * 詳細表示
   */
  const showDetails = useCallback((record: MedicalReservationRecord) => {
    logger.debug('useOutOfHoursMedicalReservationList', 'showDetails', {
      recordId: record.id
    });

    setState(prev => ({
      ...prev,
      selectedRecord: record,
      detailModalOpen: true,
    }));
  }, []);

  /**
   * 振り分けモーダル表示
   */
  const showAssignModal = useCallback((record: MedicalReservationRecord) => {
    logger.debug('useOutOfHoursMedicalReservationList', 'showAssignModal', {
      recordId: record.id
    });

    setState(prev => ({
      ...prev,
      selectedRecord: record,
      assignModalOpen: true,
    }));
  }, []);

  /**
   * モーダル閉じる
   */
  const closeModals = useCallback(() => {
    setState(prev => ({
      ...prev,
      selectedRecord: null,
      detailModalOpen: false,
      editModalOpen: false,
      assignModalOpen: false,
    }));
  }, []);

  /**
   * レコード削除
   */
  const deleteRecord = useCallback(async (id: string): Promise<boolean> => {
    const timer = logger.startTimer('useOutOfHoursMedicalReservationList', 'deleteRecord');

    try {
      await medicalReservationApiService.deleteMedicalReservation(id);

      logger.info('useOutOfHoursMedicalReservationList', 'deleteRecord:成功', { id });

      // データ再取得
      await fetchData();

      timer({ success: true, id });
      return true;
    } catch (error) {
      logger.error('useOutOfHoursMedicalReservationList', 'deleteRecord:エラー', error, { id });
      timer({ success: false, id });
      return false;
    }
  }, [fetchData]);

  /**
   * レコード振り分け
   */
  const assignRecord = useCallback(async (id: string, assignedTo: string, notes?: string): Promise<boolean> => {
    const timer = logger.startTimer('useOutOfHoursMedicalReservationList', 'assignRecord');

    try {
      await medicalReservationApiService.updateMedicalReservation(id, {
        assignedTo,
        notes,
      });

      logger.info('useOutOfHoursMedicalReservationList', 'assignRecord:成功', {
        id,
        assignedTo,
        notes
      });

      // 楽観的更新
      setState(prev => ({
        ...prev,
        data: prev.data.map(record =>
          record.id === id
            ? { ...record, assignedTo, notes }
            : record
        ),
      }));

      closeModals();

      timer({ success: true, id, assignedTo });
      return true;
    } catch (error) {
      logger.error('useOutOfHoursMedicalReservationList', 'assignRecord:エラー', error, {
        id,
        assignedTo
      });
      timer({ success: false, id });
      return false;
    }
  }, [closeModals]);

  /**
   * 身分証明書確認フラグ切り替え
   */
  const toggleIdentityVerified = useCallback(async (id: string, currentValue: boolean) => {
    try {
      await medicalReservationApiService.updateMedicalReservation(id, {
        identity_verification_flag: !currentValue,
      });

      // 楽観的更新
      setState(prev => ({
        ...prev,
        data: prev.data.map(record =>
          record.id === id
            ? { ...record, identity_verification_flag: !currentValue }
            : record
        ),
      }));

      return true;
    } catch (error) {
      console.error('身分証明書確認フラグ更新エラー:', error);
      return false;
    }
  }, []);

  /**
   * エラークリア
   */
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  /**
   * データ更新
   */
  const refresh = useCallback(() => {
    fetchData();
  }, [fetchData]);

  /**
   * レコード選択切り替え
   */
  const toggleSelectRecord = useCallback((id: string) => {
    setState(prev => ({
      ...prev,
      selectedIds: prev.selectedIds.includes(id)
        ? prev.selectedIds.filter(selectedId => selectedId !== id)
        : [...prev.selectedIds, id],
    }));
  }, []);

  /**
   * 全レコード選択
   * グレイアウトされていないレコード（過去の診療時間 AND 貼り付け済み以外）のみを選択
   */
  const selectAllRecords = useCallback(() => {
    setState(prev => ({
      ...prev,
      selectedIds: prev.data
        .filter(record => {
          // グレイアウト条件を判定（過去 AND 貼り付け済み）
          const isPast = isPastConsultation(record.consultationStartTime);
          const isPasted = record.paste_flag === true;
          return !(isPast && isPasted);
        })
        .map(record => record.id),
    }));
  }, [isPastConsultation]);

  /**
   * 選択クリア
   */
  const clearSelection = useCallback(() => {
    setState(prev => ({
      ...prev,
      selectedIds: [],
    }));
  }, []);

  /**
   * 貼り付けダイアログを開く
   */
  const openPasteDialog = useCallback(() => {
    setPasteDialogOpen(true);
    setPasteStatus(null);
  }, []);

  /**
   * 貼り付けダイアログを閉じる
   */
  const closePasteDialog = useCallback(() => {
    setPasteDialogOpen(false);
    setPasteStatus(null);
    setIsProcessing(false);
  }, []);

  /**
   * 選択したレコードを貼り付け
   */
  const pasteSelectedRecords = useCallback(async (): Promise<void> => {
    const timer = logger.startTimer('useOutOfHoursMedicalReservationList', 'pasteSelectedRecords');

    if (state.selectedIds.length === 0) {
      logger.warn('useOutOfHoursMedicalReservationList', 'pasteSelectedRecords:中止', {
        理由: '選択されたレコードがありません'
      });
      return;
    }

    logger.info('useOutOfHoursMedicalReservationList', 'pasteSelectedRecords:開始', {
      selectedIds: state.selectedIds,
      selectedCount: state.selectedIds.length,
      records: state.data.filter(r => state.selectedIds.includes(r.id)).map(r => ({
        id: r.id,
        anybotId: r.anybotId,
        doctorInCharge: r.doctorInCharge,
        consultationStartTime: r.consultationStartTime
      }))
    });

    setIsProcessing(true);

    try {
      // 貼り付けAPI呼び出し
      logger.debug('useOutOfHoursMedicalReservationList', 'pasteSelectedRecords:API呼び出し', {
        selectedIds: state.selectedIds
      });

      const response = await medicalReservationApiService.pasteToSpreadsheet(state.selectedIds);

      logger.info('useOutOfHoursMedicalReservationList', 'pasteSelectedRecords:API応答', {
        success: response.success,
        jobId: response.data?.job_id,
        totalCount: response.data?.total_count
      });

      if (response.success && response.data?.job_id) {
        // ポーリングを開始
        logger.debug('useOutOfHoursMedicalReservationList', 'pasteSelectedRecords:ポーリング開始', {
          jobId: response.data.job_id
        });

        const finalStatus = await medicalReservationApiService.pollPasteStatus(
          response.data.job_id,
          (status) => {
            logger.debug('useOutOfHoursMedicalReservationList', 'pasteSelectedRecords:進捗更新', {
              jobId: response.data!.job_id,
              status: status.status,
              progress: status.progress,
              completedCount: status.completed_count
            });
            setPasteStatus(status);
          }
        );

        logger.info('useOutOfHoursMedicalReservationList', 'pasteSelectedRecords:処理完了', {
          status: finalStatus.status,
          totalCount: finalStatus.total_count,
          completedCount: finalStatus.completed_count,
          results: finalStatus.results
        });

        setPasteStatus(finalStatus);

        // 成功した場合、リスト更新完了フラグを更新
        if (finalStatus.status === 'completed') {
          // フラグを更新して再取得
          const selectedCount = state.selectedIds.length;

          logger.debug('useOutOfHoursMedicalReservationList', 'pasteSelectedRecords:フラグ更新', {
            updatingRecords: state.selectedIds
          });

          // データ更新
          setState(prev => ({
            ...prev,
            data: prev.data.map(record =>
              state.selectedIds.includes(record.id)
                ? { ...record, paste_flag: true, actionStatus: 'completed' as ActionStatus }
                : record
            ),
          }));

          // 選択状態をクリア
          clearSelection();

          timer({
            success: true,
            selectedCount,
            jobId: response.data!.job_id
          });
        }
      } else {
        const errorMessage = response.message || '貼り付け処理の開始に失敗しました';
        logger.error('useOutOfHoursMedicalReservationList', 'pasteSelectedRecords:開始失敗',
          new Error(errorMessage), {
            response
        });
      }
    } catch (error) {
      const errorMessage = handleApiError(error);

      logger.error('useOutOfHoursMedicalReservationList', 'pasteSelectedRecords:エラー', error, {
        selectedIds: state.selectedIds,
        errorMessage
      });

      setPasteStatus({
        job_id: '',
        status: 'failed',
        progress: 0,
        total_count: 0,
        completed_count: 0,
        error_message: errorMessage,
      });

      timer({
        success: false,
        error: errorMessage
      });
    } finally {
      setIsProcessing(false);
    }
  }, [state.selectedIds, state.data, clearSelection]);

  // 初期データ取得
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data: state.data,
    meta: state.meta,
    isLoading: state.isLoading,
    error: state.error,
    filters: state.filters,
    selectedRecord: state.selectedRecord,
    detailModalOpen: state.detailModalOpen,
    assignModalOpen: state.assignModalOpen,
    selectedIds: state.selectedIds,
    updateFilters,
    handlePageChange,
    handlePageSizeChange,
    handleSort,
    handleSearch,
    refresh,
    showDetails,
    showAssignModal,
    closeModals,
    deleteRecord,
    assignRecord,
    toggleIdentityVerified,
    clearError,
    toggleSelectRecord,
    selectAllRecords,
    clearSelection,
    pasteDialogOpen,
    pasteStatus,
    isProcessing,
    openPasteDialog,
    closePasteDialog,
    pasteSelectedRecords,
    isPastConsultation,
  };
};

export default useOutOfHoursMedicalReservationList;